// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

export { AzureKeyCredential } from "@azure/core-auth";
export {
  AutocompleteItem,
  AutocompleteMode,
  AutocompleteResult,
  FacetResult,
  IndexActionType,
  IndexDocumentsResult,
  IndexingResult,
  KnownSemanticErrorMode,
  KnownSemanticErrorReason,
  KnownSemanticSearchResultsType,
  KnownVectorFilterMode,
  KnownVectorQueryKind,
  QueryAnswerResult,
  QueryCaptionResult,
  QueryType,
  ScoringStatistics,
  SearchMode,
} from "./generated/data/models/index.js";
export {
  AnalyzeResult,
  AnalyzedTokenInfo,
  AsciiFoldingTokenFilter,
  AzureActiveDirectoryApplicationCredentials,
  AzureOpenAIModelName,
  BaseLexicalNormalizer,
  BM25Similarity,
  BinaryQuantizationCompression,
  CharFilter as BaseCharFilter,
  CharFilterName,
  CjkBigramTokenFilter,
  CjkBigramTokenFilterScripts,
  ClassicSimilarity,
  ClassicTokenizer,
  CognitiveServicesAccount as BaseCognitiveServicesAccount,
  CognitiveServicesAccountKey,
  CommonGramTokenFilter,
  ConditionalSkill,
  CorsOptions,
  CustomEntity,
  CustomEntityAlias,
  CustomLexicalNormalizer,
  DataChangeDetectionPolicy as BaseDataChangeDetectionPolicy,
  DataDeletionDetectionPolicy as BaseDataDeletionDetectionPolicy,
  DefaultCognitiveServicesAccount,
  DictionaryDecompounderTokenFilter,
  DistanceScoringFunction,
  DistanceScoringParameters,
  DocumentExtractionSkill,
  DocumentIntelligenceLayoutSkill,
  DocumentIntelligenceLayoutSkillChunkingProperties,
  DocumentIntelligenceLayoutSkillChunkingUnit,
  DocumentIntelligenceLayoutSkillExtractionOptions,
  DocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  DocumentIntelligenceLayoutSkillOutputFormat,
  DocumentIntelligenceLayoutSkillOutputMode,
  EdgeNGramTokenFilterSide,
  EdgeNGramTokenizer,
  ElisionTokenFilter,
  EntityLinkingSkill,
  EntityRecognitionSkillV3,
  FieldMapping,
  FieldMappingFunction,
  FreshnessScoringFunction,
  FreshnessScoringParameters,
  HighWaterMarkChangeDetectionPolicy,
  IndexProjectionMode,
  IndexerExecutionResult,
  IndexerExecutionStatus,
  IndexerStatus,
  IndexingSchedule,
  InputFieldMappingEntry,
  KeepTokenFilter,
  KeywordMarkerTokenFilter,
  KnownAzureOpenAIModelName,
  KnownBlobIndexerDataToExtract,
  KnownBlobIndexerImageAction,
  KnownBlobIndexerPDFTextRotationAlgorithm,
  KnownBlobIndexerParsingMode,
  KnownCharFilterName as KnownCharFilterNames,
  KnownCustomEntityLookupSkillLanguage,
  KnownDocumentIntelligenceLayoutSkillChunkingUnit,
  KnownDocumentIntelligenceLayoutSkillExtractionOptions,
  KnownDocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  KnownDocumentIntelligenceLayoutSkillOutputFormat,
  KnownDocumentIntelligenceLayoutSkillOutputMode,
  KnownEntityCategory,
  KnownEntityRecognitionSkillLanguage,
  KnownImageAnalysisSkillLanguage,
  KnownImageDetail,
  KnownIndexProjectionMode,
  KnownIndexerExecutionEnvironment,
  KnownKeyPhraseExtractionSkillLanguage,
  KnownLexicalNormalizerName as KnownLexicalNormalizerNames,
  KnownLexicalTokenizerName as KnownTokenizerNames,
  KnownOcrLineEnding,
  KnownOcrSkillLanguage,
  KnownPIIDetectionSkillMaskingMode,
  KnownRankingOrder,
  KnownRegexFlags,
  KnownSearchFieldDataType,
  KnownSearchIndexerDataSourceType,
  KnownSentimentSkillLanguage,
  KnownSplitSkillLanguage,
  KnownTextSplitMode,
  KnownTextTranslationSkillLanguage,
  KnownTokenFilterName as KnownTokenFilterNames,
  KnownVectorEncodingFormat,
  KnownVectorSearchAlgorithmKind,
  KnownVectorSearchAlgorithmMetric,
  KnownVectorSearchCompressionKind,
  KnownVectorSearchCompressionRescoreStorageMethod,
  KnownVectorSearchCompressionTarget,
  KnownVectorSearchVectorizerKind,
  KnownVisualFeature,
  LanguageDetectionSkill,
  LengthTokenFilter,
  LexicalAnalyzer as BaseLexicalAnalyzer,
  LexicalAnalyzerName,
  LexicalNormalizerName,
  LexicalTokenizer as BaseLexicalTokenizer,
  LexicalTokenizerName,
  LimitTokenFilter,
  LuceneStandardAnalyzer,
  MagnitudeScoringFunction,
  MagnitudeScoringParameters,
  MappingCharFilter,
  MergeSkill,
  MicrosoftLanguageStemmingTokenizer,
  MicrosoftLanguageTokenizer,
  MicrosoftStemmingTokenizerLanguage,
  MicrosoftTokenizerLanguage,
  NGramTokenizer,
  OcrLineEnding,
  OutputFieldMappingEntry,
  PathHierarchyTokenizerV2 as PathHierarchyTokenizer,
  PatternCaptureTokenFilter,
  PatternReplaceCharFilter,
  PatternReplaceTokenFilter,
  PhoneticEncoder,
  PhoneticTokenFilter,
  RankingOrder,
  RescoringOptions,
  ResourceCounter,
  ScalarQuantizationCompression,
  ScalarQuantizationParameters,
  ScoringFunction as BaseScoringFunction,
  ScoringFunctionAggregation,
  ScoringFunctionInterpolation,
  SearchIndexerDataContainer,
  SearchIndexerDataIdentity as BaseSearchIndexerDataIdentity,
  SearchIndexerDataNoneIdentity,
  SearchIndexerDataUserAssignedIdentity,
  SearchIndexerError,
  SearchIndexerIndexProjectionSelector,
  SearchIndexerKnowledgeStoreBlobProjectionSelector,
  SearchIndexerKnowledgeStoreFileProjectionSelector,
  SearchIndexerKnowledgeStoreObjectProjectionSelector,
  SearchIndexerKnowledgeStoreProjection,
  SearchIndexerKnowledgeStoreProjectionSelector,
  SearchIndexerKnowledgeStoreTableProjectionSelector,
  SearchIndexerLimits,
  SearchIndexerSkill as BaseSearchIndexerSkill,
  SearchIndexerStatus,
  SearchIndexerWarning,
  SemanticConfiguration,
  SemanticField,
  SemanticPrioritizedFields,
  SemanticSearch,
  SentimentSkillV3,
  ServiceCounters,
  ServiceLimits,
  ShaperSkill,
  ShingleTokenFilter,
  Similarity,
  SnowballTokenFilter,
  SnowballTokenFilterLanguage,
  SoftDeleteColumnDeletionDetectionPolicy,
  SqlIntegratedChangeTrackingPolicy,
  StemmerOverrideTokenFilter,
  StemmerTokenFilter,
  StemmerTokenFilterLanguage,
  StopAnalyzer,
  StopwordsList,
  StopwordsTokenFilter,
  Suggester as SearchSuggester,
  SynonymTokenFilter,
  TagScoringFunction,
  TagScoringParameters,
  TextWeights,
  TokenCharacterKind,
  TokenFilter as BaseTokenFilter,
  TokenFilterName,
  TruncateTokenFilter,
  UaxUrlEmailTokenizer,
  UniqueTokenFilter,
  VectorEncodingFormat,
  VectorSearchCompression as BaseVectorSearchCompression,
  VectorSearchCompressionKind,
  VectorSearchCompressionRescoreStorageMethod,
  VectorSearchCompressionTarget,
  VectorSearchProfile,
  VectorSearchVectorizerKind,
  WordDelimiterTokenFilter,
} from "./generated/service/models/index.js";
export { default as GeographyPoint } from "./geographyPoint.js";
export { IndexDocumentsBatch } from "./indexDocumentsBatch.js";
export {
  AutocompleteOptions,
  AutocompleteRequest,
  BaseSearchRequestOptions,
  BaseVectorQuery,
  CountDocumentsOptions,
  DeleteDocumentsOptions,
  ExcludedODataTypes,
  ExtractDocumentKey,
  ExtractiveQueryAnswer,
  ExtractiveQueryCaption,
  GetDocumentOptions,
  IndexDocumentsAction,
  IndexDocumentsOptions,
  ListSearchResultsPageSettings,
  MergeDocumentsOptions,
  MergeOrUploadDocumentsOptions,
  NarrowedModel,
  QueryAnswer,
  QueryCaption,
  SearchDocumentsPageResult,
  SearchDocumentsResult,
  SearchDocumentsResultBase,
  SearchFieldArray,
  SearchIndexingBufferedSenderDeleteDocumentsOptions,
  SearchIndexingBufferedSenderFlushDocumentsOptions,
  SearchIndexingBufferedSenderMergeDocumentsOptions,
  SearchIndexingBufferedSenderMergeOrUploadDocumentsOptions,
  SearchIndexingBufferedSenderOptions,
  SearchIndexingBufferedSenderUploadDocumentsOptions,
  SearchIterator,
  SearchOptions,
  SearchPick,
  SearchRequestOptions,
  SearchRequestQueryTypeOptions,
  SearchResult,
  SelectArray,
  SelectFields,
  SemanticErrorMode,
  SemanticErrorReason,
  SemanticSearchOptions,
  SemanticSearchResultsType,
  SuggestDocumentsResult,
  SuggestNarrowedModel,
  SuggestOptions,
  SuggestRequest,
  SuggestResult,
  UnionToIntersection,
  UploadDocumentsOptions,
  VectorFilterMode,
  VectorizableTextQuery,
  VectorizedQuery,
  VectorQuery,
  VectorQueryKind,
  VectorSearchOptions,
} from "./indexModels.js";
export { odata } from "./odata.js";
export { KnownSearchAudience } from "./searchAudience.js";
export { SearchClient, SearchClientOptions } from "./searchClient.js";
export { SearchIndexClient, SearchIndexClientOptions } from "./searchIndexClient.js";
export { SearchIndexerClient, SearchIndexerClientOptions } from "./searchIndexerClient.js";
export {
  DEFAULT_BATCH_SIZE,
  DEFAULT_FLUSH_WINDOW,
  DEFAULT_RETRY_COUNT,
  IndexDocumentsClient,
  SearchIndexingBufferedSender,
} from "./searchIndexingBufferedSender.js";
export {
  AnalyzeRequest,
  AnalyzeTextOptions,
  AzureOpenAIEmbeddingSkill,
  AzureOpenAIParameters,
  AzureOpenAIVectorizer,
  BaseVectorSearchAlgorithmConfiguration,
  BaseVectorSearchVectorizer,
  BlobIndexerDataToExtract,
  BlobIndexerImageAction,
  BlobIndexerParsingMode,
  BlobIndexerPDFTextRotationAlgorithm,
  CharFilter,
  CognitiveServicesAccount,
  ComplexDataType,
  ComplexField,
  CreateDataSourceConnectionOptions,
  CreateIndexerOptions,
  CreateIndexOptions,
  CreateorUpdateDataSourceConnectionOptions,
  CreateorUpdateIndexerOptions,
  CreateOrUpdateIndexOptions,
  CreateOrUpdateSkillsetOptions,
  CreateOrUpdateSynonymMapOptions,
  CreateSkillsetOptions,
  CreateSynonymMapOptions,
  CustomAnalyzer,
  CustomEntityLookupSkill,
  CustomEntityLookupSkillLanguage,
  DataChangeDetectionPolicy,
  DataDeletionDetectionPolicy,
  DeleteDataSourceConnectionOptions,
  DeleteIndexerOptions,
  DeleteIndexOptions,
  DeleteSkillsetOptions,
  DeleteSynonymMapOptions,
  EdgeNGramTokenFilter,
  EntityCategory,
  EntityRecognitionSkill,
  EntityRecognitionSkillLanguage,
  ExhaustiveKnnAlgorithmConfiguration,
  ExhaustiveKnnParameters,
  GetDataSourceConnectionOptions,
  GetIndexerOptions,
  GetIndexerStatusOptions,
  GetIndexOptions,
  GetIndexStatisticsOptions,
  GetServiceStatisticsOptions,
  GetSkillSetOptions,
  GetSynonymMapsOptions,
  HnswAlgorithmConfiguration,
  HnswParameters,
  ImageAnalysisSkill,
  ImageAnalysisSkillLanguage,
  ImageDetail,
  IndexerExecutionEnvironment,
  IndexingParameters,
  IndexingParametersConfiguration,
  IndexIterator,
  IndexNameIterator,
  KeyPhraseExtractionSkill,
  KeyPhraseExtractionSkillLanguage,
  KeywordTokenizer,
  KnownAnalyzerNames,
  LexicalAnalyzer,
  LexicalNormalizer,
  LexicalTokenizer,
  ListDataSourceConnectionsOptions,
  ListIndexersOptions,
  ListIndexesOptions,
  ListSkillsetsOptions,
  ListSynonymMapsOptions,
  LuceneStandardTokenizer,
  NGramTokenFilter,
  OcrSkill,
  OcrSkillLanguage,
  PatternAnalyzer,
  PatternTokenizer,
  PIIDetectionSkill,
  PIIDetectionSkillMaskingMode,
  RegexFlags,
  ResetIndexerOptions,
  RunIndexerOptions,
  ScoringFunction,
  ScoringProfile,
  SearchField,
  SearchFieldDataType,
  SearchIndex,
  SearchIndexer,
  SearchIndexerDataIdentity,
  SearchIndexerDataSourceConnection,
  SearchIndexerDataSourceType,
  SearchIndexerIndexProjection,
  SearchIndexerIndexProjectionParameters,
  SearchIndexerKnowledgeStore,
  SearchIndexerKnowledgeStoreParameters,
  SearchIndexerSkill,
  SearchIndexerSkillset,
  SearchIndexStatistics,
  SearchResourceEncryptionKey,
  SearchServiceStatistics,
  SentimentSkill,
  SentimentSkillLanguage,
  SimilarityAlgorithm,
  SimpleField,
  SplitSkill,
  SplitSkillLanguage,
  SynonymMap,
  TextSplitMode,
  TextTranslationSkill,
  TextTranslationSkillLanguage,
  TokenFilter,
  VectorSearch,
  VectorSearchAlgorithmConfiguration,
  VectorSearchAlgorithmKind,
  VectorSearchAlgorithmMetric,
  VectorSearchCompression,
  VectorSearchVectorizer,
  VisualFeature,
  WebApiParameters,
  WebApiSkill,
  WebApiVectorizer,
} from "./serviceModels.js";
export { createSynonymMapFromFile } from "./synonymMapHelper.js";
